import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { useSession } from '@/context/session-context';

export function LoginPage() {
  const { authSession, staffSession, loading, error, signIn, signInWithPassword, signOut } = useSession();
  const [pending, setPending] = useState<'password' | 'google' | 'apple' | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  if (!loading && staffSession) return <Navigate to="/" replace />;

  const handlePasswordSignIn = async (event: FormEvent) => {
    event.preventDefault();
    setPending('password');
    setLocalError(null);
    try {
      await signInWithPassword(email.trim(), password);
    } catch (nextError) {
      setLocalError(nextError instanceof Error ? nextError.message : 'Unable to sign in');
      setPending(null);
    }
  };

  const handleSignIn = async (provider: 'google' | 'apple') => {
    setPending(provider);
    setLocalError(null);
    try {
      await signIn(provider);
    } catch (nextError) {
      setLocalError(nextError instanceof Error ? nextError.message : 'Unable to sign in');
      setPending(null);
    }
  };

  return (
    <main className="login-page">
      <section className="login-card">
        <div className="login-brand"><span className="brand-mark">e</span></div>
        <div>
          <h1>Everyvisit Admin</h1>
          <p>Secure operations, support, and trust management.</p>
        </div>
        {authSession && error ? (
          <div className="access-denied">
            <strong>Access not granted</strong>
            <p>{error}</p>
            <button className="button secondary" onClick={() => void signOut()}>Sign in with another account</button>
          </div>
        ) : (
          <div className="login-actions">
            <form className="login-form" onSubmit={(event) => void handlePasswordSignIn(event)}>
              <label className="field">
                Email
                <input
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </label>
              <label className="field">
                Password
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </label>
              <button className="button primary" type="submit" disabled={Boolean(pending)}>
                {pending === 'password' ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
            <div className="login-divider">or</div>
            <button className="button google" onClick={() => void handleSignIn('google')} disabled={Boolean(pending)}>
              {pending === 'google' ? 'Opening Google…' : 'Continue with Google'}
            </button>
            <button className="button apple" onClick={() => void handleSignIn('apple')} disabled={Boolean(pending)}>
              {pending === 'apple' ? 'Opening Apple…' : 'Continue with Apple'}
            </button>
          </div>
        )}
        {localError ? <p className="inline-error">{localError}</p> : null}
        <small>Only approved Everyvisit platform staff can continue.</small>
      </section>
    </main>
  );
}

export function AuthCallbackPage() {
  const { loading, staffSession, error } = useSession();
  if (staffSession) return <Navigate to="/" replace />;
  if (error) {
    return (
      <main className="login-page">
        <section className="login-card">
          <div className="access-denied">
            <strong>Sign-in did not finish</strong>
            <p>{error}</p>
            <Link className="button secondary" to="/login">Back to login</Link>
          </div>
        </section>
      </main>
    );
  }
  return <main className="login-page"><div className="state-card"><span className="spinner" />Completing sign-in…</div></main>;
}
